import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  PaDataTable,
  PaEmptyState,
  PaFilterChip,
  PaSkeleton,
  PaStatusBadge,
} from "components/platform-admin";
import {
  buildRecentPaymentRows,
  filterRecentPayments,
  formatCurrency,
  formatInvoiceStatusLabel,
  RECENT_PAYMENT_FILTERS,
} from "./billingOverview.utils";
import { buildStripePath } from "../platformAdminNavigation.utils";

const getPaymentStatusVariant = (payment) => {
  if (payment.is_paid) {
    return "success";
  }

  switch (payment.status) {
    case "open":
    case "draft":
      return "warning";
    case "uncollectible":
      return "danger";
    default:
      return "neutral";
  }
};

const FinancialRecentPayments = ({
  recentPayments = null,
  loading = false,
}) => {
  const [paymentFilter, setPaymentFilter] = useState("all");

  const rows = useMemo(() => {
    const payments = recentPayments?.payments || [];
    return buildRecentPaymentRows(filterRecentPayments(payments, paymentFilter));
  }, [paymentFilter, recentPayments]);

  const currency = recentPayments?.currency || "AUD";

  return (
    <section
      id="recent-payments"
      className="scroll-mt-24 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Recent payments</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Latest paid and outstanding invoices from synced Stripe records. Open a tenant for full
            payment history.
          </p>
        </div>
        <Link
          to={buildStripePath({ tab: "sync", focus: "collected_all_time" })}
          className="shrink-0 text-xs font-medium text-indigo-600 hover:text-indigo-700"
        >
          Invoice sync →
        </Link>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {RECENT_PAYMENT_FILTERS.map((filter) => (
          <PaFilterChip
            key={filter.key}
            active={paymentFilter === filter.key}
            onClick={() => setPaymentFilter(filter.key)}
          >
            {filter.label}
          </PaFilterChip>
        ))}
      </div>

      {loading ? (
        <PaSkeleton variant="table" lines={6} />
      ) : rows.length === 0 ? (
        <PaEmptyState
          title="No payment activity"
          message={
            paymentFilter === "all"
              ? "Sync Stripe invoices to populate recent payment history."
              : `No ${paymentFilter} invoices in synced records.`
          }
          action={
            <Link
              to={buildStripePath({ tab: "sync" })}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
            >
              Open Stripe sync
            </Link>
          }
        />
      ) : (
        <PaDataTable
          columns={[
            {
              key: "event_at",
              label: "Date",
              render: (row) =>
                row.event_at ? moment(row.event_at).format("D MMM YYYY") : "—",
            },
            {
              key: "tenant_name",
              label: "Tenant",
              render: (row) =>
                row.tenant_id ? (
                  <Link
                    to={`/tenants/${row.tenant_id}?tab=billing`}
                    className="font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {row.tenant_name || `Tenant #${row.tenant_id}`}
                  </Link>
                ) : (
                  "—"
                ),
            },
            {
              key: "invoice_number",
              label: "Invoice",
              render: (row) => (
                <span className="font-mono text-xs text-gray-700">
                  {row.invoice_number || row.invoice_id || "—"}
                </span>
              ),
            },
            {
              key: "status",
              label: "Status",
              render: (row) => (
                <PaStatusBadge
                  label={formatInvoiceStatusLabel(row.status)}
                  variant={getPaymentStatusVariant(row)}
                />
              ),
            },
            {
              key: "amount_cents",
              label: "Amount",
              headerClassName: "text-right",
              cellClassName: "text-right tabular-nums",
              render: (row) => formatCurrency(row.amount_cents, row.currency || currency),
            },
          ]}
          rows={rows}
          rowKey="id"
        />
      )}
    </section>
  );
};

export default FinancialRecentPayments;
